import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { AlertsMenuQueryGraphql } from "@custom/base/components/alerts/alerts/alerts-menu-query.graphql";
import { GraphqlService } from "@lib/core/modules/graphql/graphql-service.service";
import { TranslatePipe } from "@ngx-translate/core";
import { combineLatest, filter, interval, map, merge, of, shareReplay, Subject, switchMap } from "rxjs";
import { Alerte, SortInput } from "@graphql-generated/graphql";
import { GraphQlFilterRecord } from "@lib/core/modules/graphql/interfaces/filter.types";
import { Pagination } from "@lib/core/modules/graphql/interfaces/pagination";
import { AsyncPipe } from "@angular/common";
import { MatCard, MatCardContent, MatCardFooter, MatCardHeader } from "@angular/material/card";
import { DateModelPipe } from "@lib/base/pipes/date-model.pipe";
import { RouterLink } from "@angular/router";
import { MatBadge } from "@angular/material/badge";
import { FixedOverlayDirective } from "@lib/base/directives/fixed-overlay.directive";
import { ConnectedPosition } from "@angular/cdk/overlay";
import { UserRolesService } from "@lib/core/services/user-roles.service";
import { closingAlertDialogClosed } from "@workspace/pages/alerts/close-alert-button/close-alert-button.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "app-alerts-menu",
    imports: [
        MatIconButton,
        MatIcon,
        AsyncPipe,
        MatCard,
        MatCardContent,
        MatCardFooter,
        DateModelPipe,
        RouterLink,
        MatBadge,
        FixedOverlayDirective,
        MatCardHeader,
        MatButton,
        TranslatePipe,

    ],
    templateUrl: "./alerts-menu.component.html",
    styleUrl: "./alerts-menu.component.scss",
})
export class AlertsMenuComponent implements OnInit, OnDestroy {
    private static readonly FifteenSeconds = 15 * 1000;

    private readonly graphqlService = inject(GraphqlService);
    private readonly roleService = inject(UserRolesService);

    private readonly componentInit = new Subject<void>();

    protected readonly canI$ = this.roleService.canI("READ", "Alerte");

    private querySettingObservable = of({
        querySetting: {
            filtering: [
                {
                    field: "statut",
                    operator: "neq",
                    value: "TERMINEE",
                    relations: [],
                },
            ] satisfies GraphQlFilterRecord,
            sorting: {
                field: "dateDebut",
                direction: "DESC",
                relations: [],
                then: {
                    field: "typeDalerte",
                    direction: "DESC",
                    nulls: "LAST",
                    relations: [],
                },
            } satisfies SortInput,
            paging: new Pagination(0, 5, true),
        },
    });

    private readonly triggers = merge(
        interval(AlertsMenuComponent.FifteenSeconds),
        closingAlertDialogClosed,
        this.componentInit,
    ).pipe(
        takeUntilDestroyed(),
    );

    protected datasource = combineLatest({
        canI: this.canI$,
        triggers: this.triggers,
    }).pipe(
        filter(({ canI }) => canI),
        switchMap(_ => this.graphqlService.singleQuery(AlertsMenuQueryGraphql)
            .observeWith(this.querySettingObservable)),
        shareReplay(),
        takeUntilDestroyed(),
    );

    protected alerts$ = this.datasource.pipe(
        map(result => result.items),
    );

    protected isCritical$ = this.datasource.pipe(
        map(result => result.items.some(alert => alert.typeDalerte === "CRITIQUE")),
    );

    protected totalCount$ = this.datasource.pipe(
        map(result => result.totalCount),
    );

    private subscription = this.datasource.subscribe();

    overlayPosition: ConnectedPosition = {
        originX: "start",
        originY: "bottom",
        overlayX: "end",
        overlayY: "top",
        offsetX: 64,
    };

    ngOnInit() {
        this.componentInit.next();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.componentInit.complete();
    }
}
